// BuyerCart.jsx
import React, { useState } from "react";
import "../styles/BuyerCart.css";
import { useQuery, gql } from "@apollo/client";

const ITEMS_PER_PAGE = 10;

const GET_ALL_CARTS = gql`
  query GetAllCarts {
    getAllCarts {
      id
      buyerId
      productId
      quantity
      addedAt
      product {
        productId
        name
        brand
        price
        image
        description
        category
      }
    }
  }
`;

const BuyerCart = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const { loading, error: queryError, data } = useQuery(GET_ALL_CARTS, {
    fetchPolicy: "network-only",
  });

  const carts = React.useMemo(() => data?.getAllCarts || [], [data]);
  const error = queryError ? queryError.message : null;

  // Get unique categories for dropdown
  const uniqueCategories = [...new Set(carts.map(cart => cart.product?.category).filter(Boolean))];

  // Format date function
  const formatDate = (date) =>
    new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // Get day name from date
  const getDayName = (date) => {
    return new Date(date).toLocaleDateString('en-IN', { weekday: 'long' });
  };

  // Sorting function
  const sortedCarts = React.useMemo(() => {
    let sortableCarts = [...carts];
    if (sortConfig.key) {
      sortableCarts.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key.includes('product.')) {
          const key = sortConfig.key.split('.')[1];
          aValue = a.product?.[key] || '';
          bValue = b.product?.[key] || '';
        } else if (sortConfig.key === 'total') {
          aValue = (a.product?.price || 0) * a.quantity;
          bValue = (b.product?.price || 0) * b.quantity;
        } else {
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableCarts;
  }, [carts, sortConfig]);

  // Request sort function
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter carts function
  const filteredCarts = sortedCarts.filter((cart) => {
    const term = searchTerm.toLowerCase();

    // Text search filter
    if (term && !(
      cart.buyerId?.toLowerCase().includes(term) ||
      cart.product?.name?.toLowerCase().includes(term) ||
      cart.product?.brand?.toLowerCase().includes(term) ||
      cart.product?.category?.toLowerCase().includes(term)
    )) {
      return false;
    }

    // Date filter (today, yesterday, last 7 days, last 30 days, custom range)
    if (dateFilter || startDate || endDate) {
      const cartDate = new Date(cart.addedAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dateFilter === "today") {
        const cartDay = new Date(cartDate);
        cartDay.setHours(0, 0, 0, 0);
        if (cartDay.getTime() !== today.getTime()) return false;
      }
      else if (dateFilter === "yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const cartDay = new Date(cartDate);
        cartDay.setHours(0, 0, 0, 0);
        if (cartDay.getTime() !== yesterday.getTime()) return false;
      }
      else if (dateFilter === "last7days") {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (cartDate < weekAgo) return false;
      }
      else if (dateFilter === "last30days") {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        if (cartDate < monthAgo) return false;
      }
      else if (dateFilter === "thisweek") {
        const firstDayOfWeek = new Date(today);
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        firstDayOfWeek.setDate(diff);
        firstDayOfWeek.setHours(0, 0, 0, 0);
        if (cartDate < firstDayOfWeek) return false;
      }
      else if (dateFilter === "thismonth") {
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        if (cartDate < firstDayOfMonth) return false;
      }
    }

    // Custom date range filter
    if (startDate || endDate) {
      const cartDate = new Date(cart.addedAt).getTime();

      if (startDate) {
        const start = new Date(startDate).getTime();
        if (cartDate < start) return false;
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (cartDate > end.getTime()) return false;
      }
    }

    // Category filter
    if (categoryFilter && cart.product?.category !== categoryFilter) {
      return false;
    }

    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCarts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredCarts.length);
  const paginatedCarts = filteredCarts.slice(startIndex, endIndex);

  // Show "Next" page if there are more items
  const hasNextPage = filteredCarts.length > currentPage * ITEMS_PER_PAGE;
  const hasPreviousPage = currentPage > 1;

  // Handle search change - reset to page 1
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Reset all filters
  const resetFilters = () => {
    setDateFilter("");
    setStartDate("");
    setEndDate("");
    setCategoryFilter("");
    setCurrentPage(1);
  };

  // Handle refresh data
  const handleRefresh = () => {
    window.location.reload(); // Simple page refresh
  };

  // Handle delete action
  const handleDelete = (cartId) => {
    if (window.confirm("Are you sure you want to delete this cart item?")) {
      console.log('Deleting cart item:', cartId);
      // Add your delete mutation here
    }
  };

  // Export function
  const exportToCSV = () => {
    if (filteredCarts.length === 0) {
      alert("No data to export!");
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Buyer ID',
      'Date Added',
      'Day',
      'Product Name',
      'Brand',
      'Category',
      'Quantity',
      'Unit Price (₹)',
      'Total Price (₹)'
    ];

    // Prepare CSV rows
    const csvRows = filteredCarts.map(cart => {
      const totalPrice = (cart.product?.price || 0) * cart.quantity;
      return [
        `"${cart.buyerId || ''}"`,
        `"${formatDate(cart.addedAt)}"`,
        `"${getDayName(cart.addedAt)}"`,
        `"${cart.product?.name || ''}"`,
        `"${cart.product?.brand || ''}"`,
        `"${cart.product?.category || ''}"`,
        cart.quantity,
        (cart.product?.price || 0).toFixed(2),
        totalPrice.toFixed(2)
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `shopping_carts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="sheet-container">
        <div className="sheet-loading">
          <div className="loading-spinner"></div>
          <p>Loading shopping cart data...</p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalValue = filteredCarts.reduce((sum, cart) =>
    sum + ((cart.product?.price || 0) * cart.quantity), 0
  );
  const totalQuantity = filteredCarts.reduce((sum, cart) => sum + cart.quantity, 0);

  return (
    <div className="sheet-container">
      <div className="sheet-header">
        <h1>
          <span className="sheet-icon">🛒</span>
          Shopping Carts Inventory
        </h1>
        <div className="sheet-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search carts..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="sheet-search"
            />

          </div>
          <div className="action-buttons">
            <button
              className="filter-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? '▲ Hide Filters' : '▼ Show Filters'}
            </button>
            <button className="refresh-btn" onClick={handleRefresh}>
              ↻ Refresh
            </button>
            <button className="export-btn" onClick={exportToCSV}>
              📥 Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="sheet-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Filters Section */}
      {showFilters && (
        <div className="filters-container">
          <div className="filters-header">
            <h3>Advanced Filters</h3>
            <button className="reset-filters-btn" onClick={resetFilters}>
              Reset All Filters
            </button>
          </div>

          <div className="filters-grid">
            {/* Date Filters */}
            <div className="filter-group">
              <label>Date Filter</label>
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisweek">This Week</option>
                <option value="thismonth">This Month</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
              </select>
            </div>

            {/* Custom Date Range */}
            <div className="filter-group">
              <label>Custom Date Range</label>
              <div className="date-range-inputs">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="date-input"
                  placeholder="Start Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="date-input"
                  placeholder="End Date"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="filter-group">
              <label>Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="">All Categories</option>
                <option value="Drone">Drone</option>
                <option value="Parts">Parts</option>
                <option value="Accessories">Accessories</option>
                <option value="Drone Parts">Drone Parts</option>
                <option value="Drone Accessories">Drone Accessories</option>
                {uniqueCategories.filter(cat =>
                  !["Drone", "Parts", "Accessories", "Drone Parts", "Drone Accessories"].includes(cat)
                ).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="sheet-summary">
        <div className="summary-card">
          <span className="summary-label">Total Carts</span>
          <span className="summary-value">{filteredCarts.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Items</span>
          <span className="summary-value">{totalQuantity}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Value</span>
          <span className="summary-value">₹{totalValue.toFixed(2)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Showing</span>
          <span className="summary-value">
            {startIndex + 1}-{endIndex} of {filteredCarts.length}
          </span>
        </div>
      </div>

      <div className="sheet-table-container">
        <div className="sheet-table-wrapper">
          <table className="sheet-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => requestSort('buyerId')}
                >
                  Buyer ID
                  {sortConfig.key === 'buyerId' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  className="sortable"
                  onClick={() => requestSort('addedAt')}
                >
                  Date Added
                  {sortConfig.key === 'addedAt' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Day</th>
                <th
                  className="sortable"
                  onClick={() => requestSort('product.name')}
                >
                  Product Name
                  {sortConfig.key === 'product.name' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  className="sortable"
                  onClick={() => requestSort('product.brand')}
                >
                  Brand
                  {sortConfig.key === 'product.brand' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  className="sortable"
                  onClick={() => requestSort('product.category')}
                >
                  Category
                  {sortConfig.key === 'product.category' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  className="sortable numeric"
                  onClick={() => requestSort('quantity')}
                >
                  Qty
                  {sortConfig.key === 'quantity' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  className="sortable numeric"
                  onClick={() => requestSort('product.price')}
                >
                  Unit Price
                  {sortConfig.key === 'product.price' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  className="sortable numeric"
                  onClick={() => requestSort('total')}
                >
                  Total Price
                  {sortConfig.key === 'total' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCarts.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-data-cell">
                    <div className="empty-state">
                      <span className="empty-icon">📭</span>
                      <p>No cart items found</p>
                      {searchTerm && <p className="empty-subtext">No results for "{searchTerm}"</p>}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedCarts.map((cart) => {
                  const totalPrice = (cart.product?.price || 0) * cart.quantity;

                  return (
                    <tr key={cart.id}>
                      <td className="buyer-cell">
                        <span className="cell-value">{cart.buyerId || "N/A"}</span>
                      </td>
                      <td className="date-cell">
                        <span className="cell-value">{formatDate(cart.addedAt)}</span>
                      </td>
                      <td className="day-cell">
                        <span className="cell-value">{getDayName(cart.addedAt)}</span>
                      </td>
                      <td className="product-cell">
                        <div className="product-info">
                          <div className="product-name">{cart.product?.name || "N/A"}</div>
                        </div>
                      </td>
                      <td>
                        <span className="cell-value">{cart.product?.brand || "N/A"}</span>
                      </td>
                      <td>
                        <span className="category-tag">{cart.product?.category || "N/A"}</span>
                      </td>
                      <td className="numeric">
                        <span className="quantity-badge">{cart.quantity}</span>
                      </td>
                      <td className="numeric">
                        <span className="price-value">₹{(cart.product?.price || 0).toFixed(2)}</span>
                      </td>
                      <td className="numeric">
                        <span className="total-value">₹{totalPrice.toFixed(2)}</span>
                      </td>
                      <td>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(cart.id)}
                          title="Delete Cart Item"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Simple Pagination - Shows only when needed */}
      {filteredCarts.length > ITEMS_PER_PAGE && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </button>

          <div className="page-info">
            Page {currentPage} of {totalPages}
            <div className="showing-info">
              Showing {startIndex + 1}-{endIndex} of {filteredCarts.length} items
            </div>
          </div>

          <button
            className="pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Show pagination info even when there are less than 10 items but multiple pages */}
      {filteredCarts.length > 0 && filteredCarts.length <= ITEMS_PER_PAGE && totalPages > 1 && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </button>

          <div className="page-info">
            Page {currentPage} of {totalPages}
          </div>

          <button
            className="pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BuyerCart;